import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import type { CreditImportController } from '../hooks/useCreditImports';

/**
 * External CPE credit import for the CPE history page — source-neutral (PICPA/Aptify
 * today, CPAx later); every string that names the source comes from the controller.
 * `CreditImportPanel` renders the one-time Yes/No prompt and, after a yes, the result
 * with the nightly-sync offer. Afterwards the Import credits page lists the source as a
 * connected row (`components/credit-import/ConnectedSourcesList.tsx`), so a user who said
 * no can still import later.
 */

function pluralActivities(n: number): string {
  return `${n} ${n === 1 ? 'activity' : 'activities'}`;
}

function ImportErrorSnackbar({ import: imp }: { import: CreditImportController }) {
  return (
    <Snackbar
      open={!!imp.error}
      autoHideDuration={5000}
      onClose={imp.clearError}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={imp.clearError} severity="error" sx={{ width: '100%' }}>
        {imp.error}
      </Alert>
    </Snackbar>
  );
}

function ImportIcon({ syncing }: { syncing: boolean }) {
  return syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />;
}

const panelSx = {
  mb: 3,
  p: 2,
  borderRadius: 1,
  bgcolor: 'tint.primarySoft',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flexWrap: 'wrap',
} as const;

/** One-time prompt (tinted panel + outlined action — house style, never an info Alert) and its follow-up. */
export function CreditImportPanel({ import: imp }: { import: CreditImportController }) {
  const { status, displayName, mode, syncing, savingPreference, lastResult, accept, decline, finish, setAutoSync } = imp;

  if (mode === 'prompt') {
    const pending = status.pendingCreditCount ?? 0;
    return (
      <>
        <Box data-testid={`credit-import-prompt-${imp.source}`} sx={panelSx}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              You have CPE credits in {displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Would you like to import them?
            </Typography>
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {pluralActivities(pending)} found for {status.email}
            </Typography>
          </Box>
          <Button variant="text" size="small" disabled={syncing} onClick={() => void decline()}>
            No
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={syncing}
            startIcon={<ImportIcon syncing={syncing} />}
            onClick={() => void accept()}
          >
            Yes, import
          </Button>
        </Box>
        <ImportErrorSnackbar import={imp} />
      </>
    );
  }

  if (mode === 'imported') {
    const imported = (lastResult?.created ?? 0) + (lastResult?.updated ?? 0);
    return (
      <>
        <Box data-testid={`credit-import-done-${imp.source}`} sx={panelSx}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Imported {pluralActivities(imported)} from {displayName}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Keep them in sync nightly? You can change this anytime in your profile.
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={status.autoSyncEnabled}
                disabled={savingPreference}
                onChange={(_, checked) => void setAutoSync(checked)}
              />
            }
            label={<Typography variant="body2">Keep in sync nightly</Typography>}
            sx={{ mr: 0 }}
          />
          <Button variant="outlined" size="small" onClick={finish}>
            Done
          </Button>
        </Box>
        <ImportErrorSnackbar import={imp} />
      </>
    );
  }

  return null;
}

export default CreditImportPanel;
